import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import { supabase } from "../supabase";
import { secureRemoveItem } from "../utils/secureStorage";
import { sendWelcomeEmail } from "../services/emailService";

// Create the auth context
const AuthContext = createContext();

// Session timeout in milliseconds (30 minutes)
const SESSION_TIMEOUT = 30 * 60 * 1000;

// Create a custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userToken, setUserToken] = useState(null);
  const [userData, setUserData] = useState(null);
  const [showWelcomeAnimation, setShowWelcomeAnimation] = useState(false);

  // Using ref for session timeout to avoid recreating timers on re-renders
  const sessionTimeoutRef = useRef(null);
  const userActivityRef = useRef(Date.now());
  const previousUserRef = useRef(null);

  // Track user activity for session timeout
  const resetActivityTimer = () => {
    userActivityRef.current = Date.now();
  };

  // Check for session timeout
  const checkSessionTimeout = useCallback(() => {
    if (!currentUser) return;

    const now = Date.now();
    const timeElapsed = now - userActivityRef.current;

    if (timeElapsed > SESSION_TIMEOUT) {
      console.log("Session timeout, logging out user");
      logout();
    }
  }, [currentUser]);

  // Setup session timeout checker
  useEffect(() => {
    const activityEvents = ["mousedown", "keydown", "touchstart", "scroll"];

    // Add event listeners for user activity
    activityEvents.forEach((event) => {
      window.addEventListener(event, resetActivityTimer);
    });

    // Set interval to check for session timeout
    const intervalId = setInterval(checkSessionTimeout, 60000); // Check every minute

    return () => {
      // Clean up event listeners and interval
      activityEvents.forEach((event) => {
        window.removeEventListener(event, resetActivityTimer);
      });
      clearInterval(intervalId);
    };
  }, [checkSessionTimeout]);

  // Get fresh token and handle token refresh
  const getToken = async (forceRefresh = false) => {
    if (!currentUser) return null;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      setUserToken(token);
      return token;
    } catch (error) {
      console.error("Error getting auth token:", error);
      return null;
    }
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      setError("");

      // Get the current origin to ensure proper redirect
      const currentOrigin = window.location.origin;
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${currentOrigin}/`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
            // Add branding hint for Google OAuth
            hd: '', // Optional: restrict to specific domain
          }
        }
      });

      if (error) throw error;

      // Reset activity timer
      resetActivityTimer();

      return {
        user: data.user,
        needsProfileCompletion: true, // Will be checked after auth state change
      };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Sign out
  const logout = async () => {
    try {
      // Clear session timeout
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
        sessionTimeoutRef.current = null;
      }

      // Clear token
      setUserToken(null);
      setUserData(null);

      // Sign out from Supabase
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Clear any stored credentials
      secureRemoveItem("authUser");
      sessionStorage.removeItem("authToken");

      return true;
    } catch (error) {
      console.error("Logout error:", error);
      throw error;
    }
  };

  // Sign up with email and password
  const signUpWithEmail = async (email, password, userData = {}) => {
    try {
      setError("");

      // Check if email is already used with Google OAuth
      const { data: existingUser, error: checkError } = await supabase
        .from('users')
        .select('id, email')
        .eq('email', email)
        .single();

      if (checkError && checkError.code !== "PGRST116") {
        throw checkError;
      }

      if (existingUser) {
        // Check if user exists in auth.users with Google provider
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (authUser && authUser.email === email) {
          throw new Error("این ایمیل قبلا با حساب Google ثبت شده است. لطفا با Google وارد شوید.");
        }
        throw new Error("این ایمیل قبلا ثبت شده است. لطفا وارد شوید.");
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            phone: userData.phone || '',
            ...userData
          },
          emailRedirectTo: `${window.location.origin}/`
        }
      });

      if (error) {
        // Handle specific Supabase errors
        if (error.message.includes('already registered')) {
          throw new Error("این ایمیل قبلا ثبت شده است. لطفا وارد شوید یا با Google وارد شوید.");
        }
        throw error;
      }

      // Create user record in users table
      if (data.user) {
        const { error: dbError } = await supabase
          .from('users')
          .insert({
            id: data.user.id,
            email: data.user.email,
            display_name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || data.user.email,
            phone: userData.phone || null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            profile_complete: false,
            wallet_balance: 0,
            lvs_points: 0,
            is_admin: false,
            client_info: {
              provider: 'email',
              user_agent: window.btoa(navigator.userAgent),
            }
          });

        if (dbError && dbError.code !== '23505') { // Ignore duplicate key errors
          console.error("Error creating user record:", dbError);
        }
      }

      resetActivityTimer();

      // Send welcome email (fire-and-forget)
      if (data.user) {
        sendWelcomeEmail({
          email: data.user.email,
          display_name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
          ...data.user,
        }).catch((err) => console.error("Failed to send welcome email:", err));
      }

      return { user: data.user, needsEmailConfirmation: !data.session };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Sign in with email and password
  const signInWithEmail = async (email, password) => {
    try {
      setError("");

      // Check if user exists in our users table with Google provider
      const { data: existingUser } = await supabase
        .from('users')
        .select('id, email, client_info')
        .eq('email', email)
        .single();

      if (existingUser && existingUser.client_info?.provider === 'google') {
        throw new Error("این ایمیل با حساب Google ثبت شده است. لطفا با Google وارد شوید.");
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // Handle specific errors
        if (error.message.includes('Invalid login credentials') || error.message.includes('Invalid login')) {
          throw new Error("ایمیل یا رمز عبور اشتباه است.");
        } else if (error.message.includes('Email not confirmed')) {
          throw new Error("لطفا ابتدا ایمیل خود را تایید کنید.");
        } else if (error.message.includes('email address') && error.message.includes('already')) {
          throw new Error("این ایمیل قبلا با Google ثبت شده است. لطفا با Google وارد شوید.");
        }
        throw error;
      }

      resetActivityTimer();
      return { user: data.user };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Password reset
  const resetPassword = async (email) => {
    try {
      setError("");
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      return true;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Update password (after reset)
  const updatePassword = async (newPassword) => {
    try {
      setError("");
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      if (error) throw error;
      return true;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Update user profile
  const updateUserProfile = async (data) => {
    try {
      if (!currentUser) throw new Error("No authenticated user");

      // Map camelCase inputs to snake_case for database while keeping auth metadata in sync
      const { displayName, phoneNumber, birthdate, gender, ...rest } = data;
      const authMetadata = {
        ...rest,
        ...(displayName ? { displayName, display_name: displayName } : {}),
        ...(phoneNumber ? { phoneNumber, phone: phoneNumber } : {}),
        ...(birthdate ? { birthdate } : {}),
        ...(gender ? { gender } : {}),
      };

      const dbUpdate = {
        ...rest,
        ...(displayName ? { display_name: displayName } : {}),
        ...(phoneNumber ? { phone: phoneNumber } : {}),
        ...(birthdate ? { birthdate } : {}),
        ...(gender ? { gender } : {}),
        updated_at: new Date().toISOString(),
      };

      // Update Supabase auth metadata
      const { error: authError } = await supabase.auth.updateUser({
        data: authMetadata
      });
      if (authError) throw authError;

      // Update user profile in database
      const { error: dbError } = await supabase
        .from('users')
        .update(dbUpdate)
        .eq('id', currentUser.id);

      if (dbError) throw dbError;

      // Force refresh the currentUser object
      setCurrentUser({ ...currentUser, ...authMetadata });
      setUserData((prev) => prev ? { ...prev, ...dbUpdate } : prev);

      return true;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Trigger welcome animation when user logs in
  const triggerWelcomeAnimation = () => {
    setShowWelcomeAnimation(true);
  };

  // Effect for auth state changes
  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        console.error("Error getting session:", error);
      } else if (session?.user) {
        setCurrentUser(session.user);
        setUserToken(session.access_token);
        sessionStorage.setItem("authToken", session.access_token);

        // Trigger welcome animation for existing session
        setTimeout(() => {
          triggerWelcomeAnimation();
        }, 500);
      }

      setLoading(false);
    };

    getInitialSession();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log("Auth state changed:", event, session?.user?.email);

        const wasLoggedOut = previousUserRef.current === null;
        const isLoggingIn = session?.user !== null && wasLoggedOut;

        setCurrentUser(session?.user || null);
        previousUserRef.current = session?.user || null;

        if (session?.user) {
          setUserToken(session.access_token);
          sessionStorage.setItem("authToken", session.access_token);

          // Reset activity timer
          resetActivityTimer();

          // Trigger welcome animation if this is a new login
          if (isLoggingIn) {
            setTimeout(() => {
              triggerWelcomeAnimation();
            }, 500);
          }
        } else {
          // Clear token when user logs out
          setUserToken(null);
          setUserData(null);
          setShowWelcomeAnimation(false);
          sessionStorage.removeItem("authToken");
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Live user profile subscription (walletBalance, lvsPoints, etc.)
  useEffect(() => {
    if (!currentUser?.id) return;

    // Fetch initial user data
    const fetchUserData = async () => {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', currentUser.id)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
        console.error("Error fetching user data:", error);
        return;
      }

      if (data) {
        setUserData(data);
      } else {
        // User doesn't exist in our users table, create it
        const userData = {
          id: currentUser.id,
          email: currentUser.email,
          display_name: currentUser.user_metadata?.full_name || currentUser.user_metadata?.name,
          photo_url: currentUser.user_metadata?.avatar_url,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          last_login: new Date().toISOString(),
          profile_complete: false,
          wallet_balance: 0,
          lvs_points: 0,
          is_admin: false,
          client_info: {
            last_login_ip: window.btoa(navigator.userAgent),
            user_agent: window.btoa(navigator.userAgent),
          }
        };

        const { error: insertError } = await supabase
          .from('users')
          .insert(userData);

        if (insertError) {
          console.error("Error creating user record:", insertError);
        } else {
          setUserData(userData);
        }
      }
    };

    fetchUserData();

    // Set up real-time subscription
    const channel = supabase
      .channel(`user_${currentUser.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'users',
          filter: `id=eq.${currentUser.id}`,
        },
        (payload) => {
          setUserData(payload.new);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUser]);

  const value = {
    currentUser,
    loading,
    error,
    userToken,
    userData,
    walletBalance: userData?.wallet_balance || userData?.walletBalance || 0,
    lvsPoints: userData?.lvs_points || userData?.lvsPoints || 0,
    showWelcomeAnimation,
    setShowWelcomeAnimation,
    signInWithGoogle,
    signUpWithEmail,
    signInWithEmail,
    logout,
    resetPassword,
    updatePassword,
    updateUserProfile,
    getToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
